import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IImportBatchListContacts } from "../../../../../models/list-import-batch";
import { GridColDef } from "@mui/x-data-grid";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import PeopleIcon from "@mui/icons-material/People";
import { ImportListContactsModal } from "./ImportListContactsModal/ImportListContactsModal";
import { IListImportContacts } from "../../../../../models/list-import-contacts";
import MainApiService from "../../../../../services/mainApi.service";

const LIST_IMPORT_CONTACTS_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/ListImportContact";
const listImportContactsService = new MainApiService<IListImportContacts>(
  LIST_IMPORT_CONTACTS_ROUTE
);

const translationPath = "page.4uDlist.listContacts.";

export const ImportListContacts = memo(() => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [showModal, setShowModal] = useState(false);
  const [contactId, setContactId] = useState<string | null>(null);
  const serviceProps = useMemo(() => {
    return {
      ListImportBatchId: id,
    };
  }, [id]);

  const handleOpenModal = useCallback((id: string) => {
    setContactId(id);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setContactId(null);
  }, []);

  const columns: GridColDef<IListImportContacts>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "document",
      headerName: t(translationPath + "document") || "Document",
      renderCell: ({ row: { document } }) => (
        <OverflowTooltip
          text={document}
          tooltip={document}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "mail",
      headerName: t(translationPath + "mail") || "Email",
      renderCell: ({ row: { mail } }) => (
        <OverflowTooltip
          text={String(mail)}
          tooltip={String(mail)}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "whatsAppNumber",
      headerName: t(translationPath + "whatsapp_number") || "WhatsApp Number",
      renderCell: ({ row: { whatsAppNumber } }) => (
        <OverflowTooltip
          text={whatsAppNumber}
          tooltip={whatsAppNumber}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "phone",
      headerName: t(translationPath + "phone") || "Phone",
      renderCell: ({ row: { phone } }) => (
        <OverflowTooltip
          text={phone}
          tooltip={phone}
          textTransform="none"
          variant="body2"
        />
      ),
    },
  ];

  return (
    <>
      <Table<IListImportContacts>
        showDefaultMacroFilters={false}
        service={listImportContactsService}
        serviceProps={serviceProps}
        columns={columns}
        title={t(translationPath + "title")}
        showButton={false}
        showDeleteButton={false}
        backButton
        searchInput
        rowActions={(params) => {
          if (params.row.id) {
            return [
              {
                action: () => handleOpenModal(params.row.id),
                icon: <PeopleIcon fontSize="small" />,
                text: t(translationPath + "contact_detail"),
              },
            ];
          }
          return [];
        }}
        searchInputPlaceHolder={
          t(translationPath + "search") || "Search for a contact"
        }
        confirmOptions={{
          title: t(translationPath + "confirm.title"),
          description: t(translationPath + "confirm.description"),
          confirmationButtonProps: {
            variant: "contained",
          },
        }}
      />

      <ImportListContactsModal
        showModal={showModal}
        onClose={handleCloseModal}
        contactId={contactId}
      />
    </>
  );
});
